import {
    CategoryScale,
    Chart,
    LinearScale,
    BarController,
    BarElement,
    LineController,
    LineElement,
    PointElement,
    Tooltip,
} from "chart.js";

Chart.register(
    CategoryScale,
    LinearScale,
    BarController,
    BarElement,
    LineController,
    LineElement,
    PointElement,
    Tooltip,
);

const buildNumberFormatter = (options = {}) =>
    new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 0,
        ...options,
    });

export const registerForecastTrendChart = (Alpine) => {
    Alpine.data(
        "forecastTrendChart",
        ({
            labels = [],
            currentValues = [],
            previousValues = [],
            currentCounts = [],
            previousCounts = [],
            currentYearLabel,
            previousYearLabel,
        }) => ({
            chart: null,
            numberFormatter: buildNumberFormatter(),
            init() {
                if (!this.$refs.canvas || labels.length === 0) {
                    return;
                }

                this.chart = new Chart(this.$refs.canvas, {
                    type: "line",
                    data: {
                        labels,
                        datasets: [
                            {
                                type: "bar",
                                label: `${currentYearLabel ?? "This year"} reservations`,
                                data: currentCounts,
                                backgroundColor: "rgba(34, 197, 94, 0.2)",
                                borderRadius: 6,
                                barThickness: 12,
                                maxBarThickness: 14,
                                barPercentage: 0.7,
                                categoryPercentage: 0.8,
                                order: 2,
                                yAxisID: "y1",
                            },
                            {
                                type: "bar",
                                label: `${previousYearLabel ?? "Last year"} reservations`,
                                data: previousCounts,
                                backgroundColor: "rgba(148, 163, 184, 0.2)",
                                borderRadius: 6,
                                barThickness: 12,
                                maxBarThickness: 14,
                                barPercentage: 0.7,
                                categoryPercentage: 0.8,
                                order: 2,
                                yAxisID: "y1",
                            },
                            {
                                label: String(currentYearLabel ?? "This year"),
                                data: currentValues,
                                borderColor: "rgba(34, 197, 94, 0.9)",
                                backgroundColor: "rgba(34, 197, 94, 0.12)",
                                borderWidth: 2,
                                tension: 0.25,
                                pointRadius: 3,
                                pointHoverRadius: 6,
                                pointHitRadius: 10,
                                order: 1,
                            },
                            {
                                label: String(previousYearLabel ?? "Last year"),
                                data: previousValues,
                                borderColor: "rgba(148, 163, 184, 0.9)",
                                backgroundColor: "rgba(148, 163, 184, 0.1)",
                                borderWidth: 2,
                                borderDash: [6, 6],
                                tension: 0.25,
                                pointRadius: 3,
                                pointHoverRadius: 6,
                                pointHitRadius: 10,
                                order: 1,
                            },
                        ],
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: {
                            mode: "index",
                            intersect: false,
                        },
                        plugins: {
                            legend: {
                                position: "bottom",
                                labels: {
                                    usePointStyle: true,
                                    pointStyle: "line",
                                },
                            },
                            tooltip: {
                                callbacks: {
                                    beforeLabel: (context) => {
                                        if (context.dataset.type !== "bar") {
                                            return null;
                                        }

                                        const firstBarIndex =
                                            context.chart.data.datasets.findIndex(
                                                (dataset) => dataset.type === "bar",
                                            );

                                        if (context.datasetIndex === firstBarIndex) {
                                            return " ";
                                        }

                                        return null;
                                    },
                                    label: (context) => {
                                        const raw = Number(context.raw ?? 0);
                                        return `${context.dataset.label}: ${this.numberFormatter.format(raw)}`;
                                    },
                                },
                            },
                        },
                        scales: {
                            y: {
                                title: {
                                    display: true,
                                    text: "Forecast",
                                },
                                ticks: {
                                    callback: (value) => {
                                        const scaled = Number(value) / 1000;
                                        if (scaled === 0) {
                                            return "0";
                                        }

                                        return `${this.numberFormatter.format(scaled)}K`;
                                    },
                                },
                                grid: {
                                    color: "rgba(148, 163, 184, 0.2)",
                                },
                            },
                            y1: {
                                position: "right",
                                title: {
                                    display: true,
                                    text: "Reservations",
                                },
                                ticks: {
                                    callback: (value) =>
                                        this.numberFormatter.format(Number(value)),
                                },
                                grid: {
                                    drawOnChartArea: false,
                                },
                            },
                            x: {
                                grid: { display: false },
                            },
                        },
                    },
                });
            },
            destroy() {
                if (this.chart) {
                    this.chart.destroy();
                    this.chart = null;
                }
            },
        }),
    );
};
